import classNames from 'classnames';
import React from 'react';
import { statusToneStyle, UDStatusTone } from 'ud-ui/components/status-tone';
import UDText from 'ud-ui/components/text';

export interface UDFilterChipProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'children'> {
  /** Тон исхода, по которому фильтрует чип. «Все» и прочие фильтры без исхода — `neutral`. */
  tone?: UDStatusTone;
  /** Фильтр выбран — `aria-pressed`, насыщенная заливка и граница. */
  selected: boolean;
  /** Подпись фильтра. */
  children: React.ReactNode;
  /** Сколько записей под фильтром; не передан — счётчика нет. */
  count?: number;
}

/**
 * Фильтр-чип: кнопка-переключатель «показать только …» со счётчиком.
 *
 * Цвет — те же переменные тона, что у `UDStatusBadge` (`ud-ui/components/status-tone`): фильтр
 * «Заявлены» и бейдж «Заявлен» одного цвета. Отличие от бейджа — поведение: это `<button>` с
 * `aria-pressed`, у него есть наведение, фокус с клавиатуры и выбранное состояние.
 *
 * Чип с исходом окрашен и невыбранным — светлой заливкой своего тона, чтобы исходы различались,
 * не выбирая ни одного. Группу (`role="group"` с именем) держит `UDFilterChipGroup`, «выбран один» — экран.
 */
const UDFilterChip = ({ tone = 'neutral', selected, count, className, style, children, ...rest }: UDFilterChipProps) => {
  return (
    <button
      {...rest}
      type="button"
      aria-pressed={selected}
      data-tone={tone}
      style={{ ...statusToneStyle(tone), ...style }}
      className={classNames(
        'rounded-pill inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 border px-3 whitespace-nowrap',
        'border-(--tone-line) bg-(--tone-surface) text-(--tone-ink) hover:border-(--tone-line-selected)',
        'aria-pressed:border-(--tone-line-selected) aria-pressed:bg-(--tone-surface-selected) aria-pressed:text-(--tone-ink-selected)',
        'focus-visible:outline-accentNaplesYellow focus-visible:outline-2 focus-visible:outline-offset-2',
        'transition-colors duration-200 ease-out motion-reduce:transition-none',
        className,
      )}
    >
      {/* `color="inherit"`: `.typo` красит текст в DarkBlue сам, и без него цвет тона кнопки
          до подписи не доходит — на тёмной заливке выбранного «Все» текст пропадал. */}
      <UDText as="span" variant="text-2" color="inherit">
        {children}
      </UDText>
      {count !== undefined && (
        <UDText as="span" variant="text-2" color="inherit" className="tabular-nums" data-testid="filter-chip-count">
          {count}
        </UDText>
      )}
    </button>
  );
};

export default React.memo(UDFilterChip);
